/**
 * QuoteProductsSection.tsx
 *
 * The Products band: Cost, Tax and Post-Tax per line.
 *
 * Tax is charged on the UNIT price and then multiplied by quantity, which is
 * what legacy does (quote-handling-ds.js:4635-4657) and is not the same as
 * taxing the line. The figures shown here are the same arithmetic the server
 * uses, shown per row; the authoritative totals come back from recalc.
 *
 * Choosing a catalog product copies its name and unit onto the line, so a quote
 * keeps the wording it was written with even after the catalog changes.
 */
import { useQuery } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { getProductCatalog, type CatalogProduct, type QuoteItem } from '@/services/quotes.service';
import { QuoteSectionCard, EmptySectionRow } from './QuoteSectionCard';
import { DraftNumberInput } from './DraftNumberInput';
import { AutoGrowTextarea } from '@/components/ui/Textarea';

interface Props {
  items: Partial<QuoteItem>[];
  taxRate: number;
  readOnly: boolean;
  /**
   * Cost and Taxable are money fields legacy gates on user level
   * (quote-items-display-new.php:258, :273 — `.nonAdminEdit`).
   */
  pricingReadOnly?: boolean;
  onChange: (items: Partial<QuoteItem>[]) => void;
}

/** Per-unit tax, then quantity — the order matters once quantity is above one. */
function lineFigures(item: Partial<QuoteItem>, taxRate: number) {
  const unit = Number(item.curRate ?? 0);
  const quantity = Number(item.quantity ?? 1);
  const tax = item.taxable ? unit * (taxRate / 100) : 0;

  return { unit, quantity, tax, postTax: (unit + tax) * quantity };
}

export function QuoteProductsSection({ items, taxRate, readOnly, pricingReadOnly = false, onChange }: Props) {
  const { data: catalog } = useQuery({
    queryKey: ['quote-product-catalog'],
    queryFn: getProductCatalog,
    staleTime: 300_000,
  });

  function patch(index: number, changes: Partial<QuoteItem>) {
    onChange(items.map((row, i) => (i === index ? { ...row, ...changes } : row)));
  }

  function add() {
    onChange([
      ...items,
      { itemName: '', description: '', curRate: 0, quantity: 1, taxable: false, costItemId: null, unit: '' },
    ]);
  }

  return (
    <QuoteSectionCard
      title="Products"
      columns={['Cost', 'Tax', 'Post-Tax']}
      onAdd={readOnly ? undefined : add}
      addLabel="Add"
    >
      {items.length === 0 ? (
        <EmptySectionRow>No products on this quote.</EmptySectionRow>
      ) : (
        <div className="space-y-3">
          {items.map((row, index) => {
            const { tax, postTax } = lineFigures(row, taxRate);

            return (
              <div key={row.quoteItemId ?? `new-${index}`} className="rounded-md border border-slate-200 p-3">
                <div className="grid gap-2 md:grid-cols-[2fr_auto_auto_auto] max-md:grid-cols-1">
                  <label className="text-xs text-slate-600">
                    Item
                    <select
                      className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm disabled:bg-slate-100"
                      value={row.costItemId ?? ''}
                      disabled={readOnly}
                      onChange={(e) => {
                        const id = e.target.value ? Number(e.target.value) : null;
                        const chosen = (catalog ?? []).find((c: CatalogProduct) => c.costItemId === id);

                        patch(index, {
                          costItemId: id,
                          ...(chosen
                            ? {
                                itemName: chosen.name,
                                unit: chosen.unit,
                                curRate: chosen.defaultRate,
                                taxable: chosen.taxable,
                              }
                            : {}),
                        });
                      }}
                    >
                      <option value="">Select one…</option>
                      {(catalog ?? []).map((c: CatalogProduct) => (
                        <option key={c.costItemId} value={c.costItemId}>
                          {c.categoryName ? `${c.categoryName} — ${c.name}` : c.name}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="text-xs text-slate-600">
                    Quantity
                    <DraftNumberInput
                      className="w-20 rounded-md border border-slate-300 px-2 py-1.5 text-sm text-right disabled:bg-slate-100"
                      integer
                      emptyValue={1}
                      maxLength={5}
                      value={row.quantity ?? 1}
                      disabled={readOnly}
                      onCommit={(quantity) => patch(index, { quantity })}
                    />
                  </label>

                  <label className="text-xs text-slate-600">
                    Cost
                    <DraftNumberInput
                      className="w-24 rounded-md border border-slate-300 px-2 py-1.5 text-sm text-right disabled:bg-slate-100"
                      value={row.curRate ?? 0}
                      disabled={readOnly || pricingReadOnly}
                      onCommit={(curRate) => patch(index, { curRate })}
                    />
                  </label>

                  <label className="text-xs text-slate-600">
                    Taxable
                    <select
                      className="rounded-md border border-slate-300 px-2 py-1.5 text-sm disabled:bg-slate-100"
                      value={row.taxable ? '1' : '0'}
                      disabled={readOnly || pricingReadOnly}
                      onChange={(e) => patch(index, { taxable: e.target.value === '1' })}
                    >
                      <option value="0">No</option>
                      <option value="1">Yes</option>
                    </select>
                  </label>
                </div>

                <div className="mt-2 grid gap-2 md:grid-cols-[1fr_auto] max-md:grid-cols-1">
                  <label className="text-xs text-slate-600">
                    Description
                    <AutoGrowTextarea
                      className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm disabled:bg-slate-100"
                      rows={2}
                      value={row.description ?? ''}
                      disabled={readOnly}
                      onChange={(e) => patch(index, { description: e.target.value })}
                    />
                  </label>

                  <div className="flex items-end gap-4 text-sm">
                    <span className="text-slate-500">
                      Tax <span className="ml-1 font-medium text-slate-700">{formatCurrency(tax)}</span>
                    </span>
                    <span className="text-slate-500">
                      Post-Tax <span className="ml-1 font-medium text-slate-700">{formatCurrency(postTax)}</span>
                    </span>
                    {!readOnly && (
                      <button
                        type="button"
                        onClick={() => onChange(items.filter((_, i) => i !== index))}
                        className="mb-0.5 rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 touch-target-44 tap-feedback"
                        aria-label="Remove product"
                      >
                        <Trash2 className="h-4 w-4 touch-target-icon" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </QuoteSectionCard>
  );
}
